import React, { Component } from 'react';
import { Button, Tabs } from '@clockout-dev/design-elements';
import Inherit from '../../core/inherit';
import withHooks from '../../core/withHooks';
import Page from '../common/page/Page';
import { SCOPES } from '../../constants/scopes.constants';
import SupportController from './support.controller';
import CreateTicketModal from './elements/CreateTicketModal';
import styles from './styles/support.module.scss';
import SupportTicketTable from './elements/SupportTicketTable';
import JiraIssuesTable from './elements/JiraIssuesTable';
import IssueDetailModal from './elements/IssueDetailModal';
import { SUPPORT_TABS, SUPPORT_TAB_PARAM } from './issueTracker.constants';

class Support extends Inherit(SupportController, Component) {
    render() {
        const {
            ticketsLoading,
            tickets,
            statusTab,
            createModalOpen,
            createSubmitting,
            newTicketSubject,
            newTicketBody,
            newTicketAttachments,
            newTicketCategory,
            newTicketPriority,
            pagination,
            supportTab,
            issueRows,
            issuesLoading,
            issuesError,
            issuesPageSize,
            issuePagination,
            issueSync,
            issueStatusFilter,
            issueView,
            issueDetailKey,
            issueDetail,
            issueDetailLoading,
            issueDetailError,
        } = this.state;

        // The strip only offers what this session holds the scope for, and the
        // content branch asks the same question rather than trusting
        // `supportTab`: the state is seeded from `?tab=` and written by the
        // Tabs component, so "which tab is active" and "which tabs may be
        // shown" are two different facts and only the second one is a
        // permission.
        const accessibleTabs = this.getAccessibleSupportTabs();
        const isIssuesTab =
            supportTab === SUPPORT_TABS.ISSUES && this.canAccessSupportTab(SUPPORT_TABS.ISSUES);
        // The board's columns, derived from the served status map on every
        // render rather than stored: the map is the source of the column order
        // and the FI-facing labels, so a copy in state could only go stale.
        // Empty off-tab so the board is not asked for columns it will not draw.
        const issueColumns = isIssuesTab ? this.issueColumnKeys() : [];

        return (
            <Page
                title={{ text: 'Support', highlight: 'Tickets' }}
                scope={SCOPES.SUPPORT_VIEW}
                description="Create and track support tickets."
                notificationContextHolder={this.props.notificationContextHolder}
                action={
                    <Button
                        size="l"
                        variant="primary"
                        color="secondary"
                        fontWeight="semibold"
                        className={styles.newTicketBtn}
                        onClick={this.handleOpenCreateModal.bind(this)}
                    >
                        New ticket
                    </Button>
                }
            >
                {/* Two tabs over one shell. KAN-567 owns the real Support panel
                    layout; this is the minimum that makes the Jira view
                    reachable, and is meant to be replaced by it.

                    Rendered only when there is a choice to make: a role without
                    `admin:jira:tickets:view` has exactly one tab, and a strip
                    with a single item is chrome that explains nothing. Support
                    still renders underneath — unlike Dashboard, this screen
                    never reaches "no accessible tab", because the tickets tab
                    carries no scope of its own. */}
                {accessibleTabs.length > 1 ? (
                    <Tabs
                        activeKey={supportTab}
                        onChange={(key) => this.handleSupportTabChange(key)}
                        searchParamKey={SUPPORT_TAB_PARAM}
                        items={accessibleTabs.map(({ key, label }) => ({ key, label }))}
                    />
                ) : null}

                {isIssuesTab ? (
                    <>
                        <JiraIssuesTable
                            rows={issueRows}
                            columns={issueColumns}
                            columnState={(key) => this.issueColumn(key)}
                            onColumnEndReached={(key) => this.handleIssueColumnEndReached(key)}
                            view={issueView}
                            onViewChange={(next) => this.handleIssueViewChange(next)}
                            loading={issuesLoading}
                            error={issuesError}
                            perPage={issuesPageSize}
                            pagination={issuePagination}
                            sync={issueSync}
                            statusFilter={issueStatusFilter}
                            selectedKey={issueDetailKey}
                            onStatusFilterChange={(key) => this.handleIssueStatusFilterChange(key)}
                            onChange={(pag) => this.handleIssuesTableChange(pag)}
                            onRowClick={(record) => this.handleIssueRowClick(record)}
                            onRetry={() => this.handleIssuesRetry()}
                            resolveStatusEntry={(status) => this.servedStatusEntry(status)}
                        />
                        <IssueDetailModal
                            issueKey={issueDetailKey}
                            issue={issueDetail}
                            loading={issueDetailLoading}
                            error={issueDetailError}
                            onClose={() => this.closeIssueDetail()}
                            onOpenTicket={(ticketId) => this.navigateTicket(ticketId)}
                            resolveStatusEntry={(status) => this.servedStatusEntry(status)}
                            resolveStageLabel={(stage) => this.servedStageLabel(stage)}
                        />
                    </>
                ) : (
                    <SupportTicketTable
                        statusTab={statusTab}
                        onStatusTabChange={(key) => this.handleStatusTabChange(key)}
                        dataSource={tickets}
                        loading={ticketsLoading}
                        pagination={pagination}
                        onChange={(pag) => this.handleTableChange(pag)}
                        onRowClick={(record) => this.handleRowClick(record)}
                    />
                )}

                <CreateTicketModal
                    open={createModalOpen}
                    submitting={createSubmitting}
                    subject={newTicketSubject}
                    body={newTicketBody}
                    attachments={newTicketAttachments}
                    category={newTicketCategory}
                    priority={newTicketPriority}
                    onSubjectChange={(v) => this.setNewTicketSubject(v)}
                    onBodyChange={(v) => this.setNewTicketBody(v)}
                    onAttachmentsChange={(v) => this.setNewTicketAttachments(v)}
                    onCategoryChange={(v) => this.setNewTicketCategory(v)}
                    onPriorityChange={(v) => this.setNewTicketPriority(v)}
                    onCancel={this.handleCloseCreateModal.bind(this)}
                    onSubmit={this.handleCreateSubmit.bind(this)}
                />
            </Page>
        );
    }
}

export default withHooks(Support);
